import { useTaskStore } from '@/stores/taskStore';
import { useUIStore } from '@/stores/uiStore';
import { formatHourLabel } from '@/lib/time';
import { spellCount } from '@/lib/numberWords';
import type { Insight, SkipReason, Task } from '@/types';

const FOURTEEN_DAYS_MS = 14 * 86_400_000;
const SEVEN_DAYS_MS = 7 * 86_400_000;
const MIN_SKIPS = 3;
const CLUSTER_THRESHOLD = 0.6;

// Human labels for the skip-reason enum — the raw values (low_energy…) must
// never reach copy. Mirrors SkipReasonGrid's labels, lowercased mid-sentence.
const REASON_LABELS: Record<SkipReason, string> = {
  low_energy: 'low energy',
  out_of_time: 'out of time',
  not_relevant: 'not relevant',
  other: 'other',
};

function pickReasonCluster(skips: Task[]): Insight | null {
  const counts = new Map<SkipReason, number>();
  for (const t of skips) {
    if (!t.skipReason) continue;
    counts.set(t.skipReason, (counts.get(t.skipReason) ?? 0) + 1);
  }
  for (const [reason, count] of counts) {
    if (count / skips.length >= CLUSTER_THRESHOLD) {
      return {
        id: `reason:${reason}`,
        kind: 'reason-cluster',
        copy: `You've skipped ${spellCount(count)} tasks marked ${REASON_LABELS[reason]}. Try moving them to mornings?`,
      };
    }
  }
  return null;
}

function pickTimeCluster(skips: Task[]): Insight | null {
  const hours = skips
    .map((t) => t.skippedAt?.getHours())
    .filter((h): h is number => h !== undefined);
  for (const h of hours) {
    const near = hours.filter((x) => Math.abs(x - h) <= 1).length;
    if (near / hours.length >= CLUSTER_THRESHOLD) {
      return {
        id: `time:${h}`,
        kind: 'time-cluster',
        copy: `You skip most tasks around ${formatHourLabel(h)}. Want a low-energy block then?`,
      };
    }
  }
  return null;
}

export function useSkipIntelligence(nowDate: Date = new Date()): Insight | null {
  const tasks = useTaskStore((s) => s.tasks);
  const dismissed = useUIStore((s) => s.dismissedInsights);
  const now = nowDate.getTime();

  const recent = tasks.filter(
    (t) =>
      t.skipReason !== undefined &&
      t.skippedAt !== undefined &&
      now - t.skippedAt.getTime() <= FOURTEEN_DAYS_MS,
  );
  if (recent.length < MIN_SKIPS) return null;

  const oldest = recent.reduce(
    (min, t) => Math.min(min, t.skippedAt?.getTime() ?? Infinity),
    Number.POSITIVE_INFINITY,
  );

  const todayMidnight = new Date(now);
  todayMidnight.setHours(0, 0, 0, 0);
  const oldestDate = new Date(oldest);
  oldestDate.setHours(0, 0, 0, 0);
  const calendarDaysDiff = (todayMidnight.getTime() - oldestDate.getTime()) / 86_400_000;
  if (calendarDaysDiff < 14) return null;

  const insight = pickReasonCluster(recent) ?? pickTimeCluster(recent);
  if (!insight) return null;

  const dismissedAt = dismissed[insight.id];
  if (dismissedAt !== undefined && now - dismissedAt < SEVEN_DAYS_MS) return null;

  return insight;
}
